import { useEffect, useState } from "react";
import { Notice, Progress } from "../components/Notice";
import { api, ApiError, type ApprovalResult, type DealerApplication } from "../lib/api";

type State =
  | { kind: "loading" }
  | { kind: "ready"; applications: DealerApplication[] }
  | { kind: "error"; message: string };

type Decision = "approve" | "decline";

/**
 * What the approver is told after a decision.
 * - `checking`: the answer was lost, so we are reading back what the server
 *   recorded. The decision is never re-sent to find out.
 * - `retry`: the read-back says the application is still pending, so the
 *   approver can decide again; that is their choice, not an automatic resend.
 */
type Note =
  | { kind: "ok"; text: string }
  | { kind: "error"; text: string }
  | { kind: "checking"; text: string }
  | { kind: "retry"; text: string; application: DealerApplication; action: Decision };

function when(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/**
 * Wording for a recorded approval. `generation_enabled` is the server's global
 * switch at decision time; absent means an older API that never said, so the
 * message promises only what was recorded. Nothing here says "can generate"
 * without a server fact behind it.
 */
export function approvedText(company: string, email: string, result: ApprovalResult): string {
  if (result.generation_enabled === true) {
    return `${company} approved. ${email} now has the dealer area and generation access; their daily allowance still applies.`;
  }
  if (result.generation_enabled === false) {
    return `${company} approved. ${email} has the dealer area, and generation access is recorded, but generation is switched off on this server right now.`;
  }
  return `${company} approved. ${email} has the dealer area, and generation access is recorded; whether they can generate depends on the generation switch.`;
}

/**
 * The approver's list of dealer applications. Rendered only when the API
 * says this SoundLayer ID is a platform admin; the API refuses everyone else
 * anyway. Approve does the whole grant in one call: business record, dealer
 * access and generation access. Nothing here needs a secret or a script.
 *
 * A decision is not idempotent from the browser's side, so a lost answer is
 * never reported as "nothing changed": the server may have committed it. The
 * only safe move is to read the list back and show what it records.
 */
export function DealerApprovals() {
  const [state, setState] = useState<State>({ kind: "loading" });
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<Note | null>(null);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let active = true;
    api
      .adminApplications()
      .then((applications) => active && setState({ kind: "ready", applications }))
      .catch((error: unknown) => {
        if (!active) return;
        setState({
          kind: "error",
          message: error instanceof ApiError ? error.message : "Applications could not be loaded.",
        });
      });
    return () => {
      active = false;
    };
  }, [reload]);

  /**
   * Read the list back after a decision whose outcome we do not know, and
   * report what the server recorded. `lead` is the honest opening the
   * approver already saw, kept so the outcome stays attached to its cause.
   */
  const reconcile = async (application: DealerApplication, action: Decision, lead: string) => {
    try {
      const applications = await api.adminApplications();
      setState({ kind: "ready", applications });
      const recorded = applications.find((a) => a.id === application.id);
      if (!recorded) {
        setNote({
          kind: "error",
          text: `${lead} ${application.company} is no longer in the list, so its outcome cannot be read here.`,
        });
        return;
      }
      if (recorded.status === "pending") {
        setNote({
          kind: "retry",
          text: `${lead} The server still has ${application.company} pending, so the decision was not recorded. Nothing was sent twice.`,
          application: recorded,
          action,
        });
        return;
      }
      setNote({
        kind: "ok",
        text: `${lead} The server has ${application.company} recorded as ${recorded.status}. The decision was not sent again.`,
      });
    } catch {
      setNote({
        kind: "error",
        text: `${lead} SoundLayer could not be reached to read back what was recorded, so ${application.company} may or may not have been decided. Check again before deciding it a second time.`,
      });
    }
  };

  const decide = async (application: DealerApplication, action: Decision) => {
    setBusy(application.id);
    setNote(null);
    try {
      const result =
        action === "approve"
          ? await api.approveApplication(application.id)
          : await api.declineApplication(application.id);
      setNote({
        kind: "ok",
        text:
          result.status === "approved"
            ? approvedText(application.company, application.email, result)
            : `${application.company} declined.`,
      });
      setReload((n) => n + 1);
    } catch (error) {
      if (error instanceof ApiError && error.answered && error.code === "not_pending") {
        // Someone else decided it, or an earlier request of ours committed and
        // its answer was lost. Either way the record is the authority.
        setNote({ kind: "checking", text: `${error.message} Checking what the server recorded…` });
        await reconcile(application, action, error.message);
      } else if (error instanceof ApiError && error.answered) {
        // The server answered and refused: applicant_not_signed_up,
        // business_ambiguous, dealer_elsewhere, a gate or a bad request.
        // Nothing was committed, so the application stays as it is.
        setNote({ kind: "error", text: error.message });
      } else {
        // No answer reached us: a dropped connection, a gateway error, or the
        // API never replied. The decision may already be committed, so say so
        // and read the record back instead of claiming nothing changed.
        const lead = "The answer was lost.";
        setNote({ kind: "checking", text: `${lead} Checking what the server recorded…` });
        await reconcile(application, action, lead);
      }
    } finally {
      setBusy(null);
    }
  };

  if (state.kind === "loading") return <Progress label="Loading applications…" />;
  if (state.kind === "error") {
    return (
      <div className="card stack">
        <h3>Dealer applications</h3>
        <Notice kind="error">{state.message}</Notice>
        <button className="btn" type="button" onClick={() => setReload((n) => n + 1)}>
          Try again
        </button>
      </div>
    );
  }

  const pending = state.applications.filter((a) => a.status === "pending");
  const decided = state.applications.filter((a) => a.status !== "pending");

  return (
    <div className="card stack">
      <div className="row spread">
        <h3>Dealer applications</h3>
        <span className="pill">{pending.length} pending</span>
      </div>
      {note && (
        <Notice
          kind={note.kind === "ok" ? "ok" : note.kind === "checking" ? "info" : "error"}
          role={note.kind === "ok" || note.kind === "checking" ? "status" : "alert"}
        >
          <span className="wrap-anywhere">{note.text}</span>
          {note.kind === "retry" && (
            <div className="row" style={{ marginTop: 8 }}>
              <button
                className="btn"
                type="button"
                disabled={busy !== null}
                onClick={() => void decide(note.application, note.action)}
              >
                Retry
              </button>
            </div>
          )}
        </Notice>
      )}
      {pending.length === 0 ? (
        <p className="muted small" style={{ margin: 0 }}>
          No applications waiting.
        </p>
      ) : (
        <ul className="list" aria-label="Pending applications">
          {pending.map((a) => (
            <li key={a.id} className="stack" style={{ gap: 6 }}>
              <div className="row spread" style={{ flexWrap: "wrap", gap: 8 }}>
                <div className="wrap-anywhere" style={{ minWidth: 0 }}>
                  <strong>{a.company}</strong>
                  <div className="muted small">
                    {a.contact_name ? `${a.contact_name} · ` : ""}
                    {a.email}
                    {a.created_at ? ` · ${when(a.created_at)}` : ""}
                  </div>
                </div>
                <span className={`pill${a.signed_up ? "" : " pill-muted"}`}>
                  {a.signed_up ? "Has a SoundLayer ID" : "No SoundLayer ID yet"}
                </span>
              </div>
              {a.message && (
                <p className="small wrap-anywhere" style={{ margin: 0 }}>
                  {a.message}
                </p>
              )}
              {!a.signed_up && (
                <p className="muted small" style={{ margin: 0 }}>
                  Approve works once they have created a SoundLayer ID with this exact email.
                </p>
              )}
              <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
                <button
                  className="btn btn-primary"
                  type="button"
                  disabled={busy !== null}
                  onClick={() => void decide(a, "approve")}
                  aria-label={`Approve ${a.company}`}
                >
                  {busy === a.id ? "One moment…" : "Approve"}
                </button>
                <button
                  className="btn"
                  type="button"
                  disabled={busy !== null}
                  onClick={() => void decide(a, "decline")}
                  aria-label={`Decline ${a.company}`}
                >
                  Decline
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
      {decided.length > 0 && (
        <details>
          <summary className="muted small">Decided ({decided.length})</summary>
          <ul className="list" aria-label="Decided applications">
            {decided.map((a) => (
              <li key={a.id} className="row spread">
                <span className="wrap-anywhere" style={{ minWidth: 0 }}>
                  {a.company} <span className="muted small">{a.email}</span>
                </span>
                <span className="muted small">
                  {a.status}
                  {a.reviewed_at ? ` · ${when(a.reviewed_at)}` : ""}
                </span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
